import type { RecurringHistory } from '@/api/recurring'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { formatShortMonth } from '@/lib/formatDate'
import { fromPaise, toPaise } from '@/lib/splitMath'

const LAST_N = 6

/**
 * Average / lowest / highest of the last six payments with a bar each against the expected
 * amount. Null when those payments were all the same — a fixed rent gains nothing from it.
 */
export function AmountVariation({
  history,
  expected,
}: {
  history: RecurringHistory
  expected: number
}) {
  const { format } = useFormatCurrency()
  const paid = history.entries
    .filter((e) => e.kind === 'PAID' && e.amount != null)
    .slice(0, LAST_N)
    .reverse()
  const paise = paid.map((e) => toPaise(e.amount!))
  if (paise.length < 2 || paise.every((p) => p === paise[0])) return null

  const average = fromPaise(Math.round(paise.reduce((a, b) => a + b, 0) / paise.length))
  const top = Math.max(...paise, toPaise(expected))
  const pct = (p: number) => `${Math.max(4, Math.round((p / top) * 100))}%`

  return (
    <section aria-label={`Last ${paid.length} payments`} className="mt-4">
      <dl className="grid grid-cols-3 gap-2">
        {[
          ['Average', average],
          ['Lowest', fromPaise(Math.min(...paise))],
          ['Highest', fromPaise(Math.max(...paise))],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg bg-[var(--fl-surface-subtle)] px-2.5 py-2">
            <dt className="text-[11px] text-[var(--fl-text-3)]">{label}</dt>
            <dd className="font-serif text-[15px] font-semibold tabular-nums text-[var(--fl-text-1)]">
              {format(value as number)}
            </dd>
          </div>
        ))}
      </dl>
      <div aria-hidden="true" className="relative mt-3 flex h-14 items-end gap-1.5">
        <span
          className="absolute inset-x-0 border-t border-dashed border-[var(--fl-text-3)]"
          style={{ bottom: pct(toPaise(expected)) }}
        />
        {paise.map((p, i) => (
          <span
            key={paid[i].date + i}
            className={
              'flex-1 rounded-t-[3px] ' +
              (i === paise.length - 1 ? 'bg-[var(--fl-income)]' : 'bg-[var(--fl-income-bg)]')
            }
            style={{ height: pct(p) }}
          />
        ))}
      </div>
      <div aria-hidden="true" className="mt-1 flex gap-1.5 text-[10px] text-[var(--fl-text-3)]">
        {paid.map((e, i) => (
          <span key={e.date + i} className="flex-1 text-center">
            {formatShortMonth(e.date.slice(0, 7))}
          </span>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-[var(--fl-text-3)]">
        Dashed line: expected {format(expected)}
      </p>
    </section>
  )
}
